<template>
    <kgr-loading v-if="loading" />
    <div
        v-else
        class="kgr-postfix kgr-chat"
    >
        <slot name="chatList">
            <div class="chatlist kgr-postfix__fixed">
                <div class="chatSearch kgr-region">
                    <input
                        v-model="search"
                        type="search"
                        :placeholder="$t('search')"
                        class="kgr-input"
                        @input="onSearch"
                        :style="chatDialogEnabled ? { width: '79%', position: 'relative', top: '-8px' } : {}"
                    />
                    <kgr-button
                        v-if="chatDialogEnabled"
                        @click="openNewChatDialog()"
                        class="addButton"
                        :title="tCreateChat"
                    >
                        <kgr-icon
                            icon-class="add"
                            big
                        />
                    </kgr-button>
                    <div
                        v-if="isKindergartenChat"
                        style="display: flex; flex-direction: row"
                        :style="!chatDialogEnabled ? { marginTop: '10px' } : {}"
                    >
                        <div style="display: flex; flex-direction: column">
                            <span class="kgr-inputGroup__label">{{ tSelectOrder }}</span>
                            <kgr-generic-select
                                v-model="selectedOrder"
                                :options="orderOptions"
                                display="name"
                                no-null-option
                                @input="reorderChatList()"
                            />
                        </div>
                        <div style="display: flex; flex-direction: column; margin-left: 10px;">
                            <span class="kgr-inputGroup__label">{{ tSelectFilter }}</span>
                            <kgr-generic-select
                                v-model="selectedFilter"
                                :options="filterOptions"
                                display="name"
                                no-null-option
                                @input="filterAndReorderChatList()"
                            />
                        </div>
                    </div>
                </div>
                <div class="chatSearchResults kgr-region kgr-scrollable">
                    <ul class="kgr-verticalMenu kgr-verticalMenu--inline kgr-verticalMenu--indicatorLeft kgr-verticalMenu--indicatorHighlight">
                        <a
                            v-for="chat in chatList"
                            :key="chat.id"
                            :class="chatListItemClasses(chat.id)"
                            :href="getChatUrl(chat.id)"
                            @click="onChatListEntryClick($event, chat)"
                        >
                            <span class="kgr-verticalMenu__itemLabel">
                                <span class="kgr-text kgr-text--small kgr-text--bold">{{ chat.name }}</span>
                                <div class="kgr-text kgr-text--extraSmall kgr-color kgr-color--gray">
                                    <span
                                        v-if="chat.users && chat.users.length > 0"
                                        class="kgr-text kgr-text--small kgr-text--bold"
                                        :title="displayAllUsers(chat)"
                                    >
                                        {{ concatAndShortenUsers(chat) }}<br />
                                    </span>
                                    <span v-if="chat.department">
                                        {{ chat.department }}
                                    </span>
                                    <span v-if="chat.lastMessageCreatedAt || chat.count > 0">
                                        {{ $t('Letzte Nachricht') }}: {{ relativeTime(chat.lastMessageCreatedAt) }}
                                    </span>
                                </div>
                            </span>
                            <div
                                v-if="isCreatable || isDeletable"
                                class="kgr-verticalMenu__itemAddendum"
                            >
                                <kgr-icon
                                    v-if="showEditIcon(chat)"
                                    icon-class="edit"
                                    :title="tEditChat"
                                    tiny
                                    @click="openEditChatDialog(chat)"
                                />
                                <kgr-icon
                                    v-if="showTerminateIcon(chat)"
                                    icon-class="block"
                                    :title="tTerminateChat"
                                    tiny
                                    @click="terminateChat(chat)"
                                />
                                <kgr-icon
                                    v-if="showDeleteIcon(chat)"
                                    icon-class="delete"
                                    :title="tDeleteChat"
                                    tiny
                                    @click="deleteChat(chat)"
                                />
                            </div>
                            <div class="kgr-verticalMenu__itemAddendum">
                                <kgr-badge
                                    v-if="chat.count"
                                    :counter="chat.count"
                                    :class="[chat.lastViewAt ? 'badgeColor' : '']"
                                />
                            </div>
                        </a>
                    </ul>
                </div>
            </div>
        </slot>
        <div class="kgr-postfix__fluid">
            <slot
                v-if="selectedChat"
                name="chatView"
                :selected-chat="{ selectedChat, author }"
            >
                <kgr-chat-view
                    :chat="selectedChat"
                    :institution-key="institutionKey"
                    :author="author"
                    @newMessage="updateChatListByNewMessage"
                />
            </slot>
            <slot
                v-else
                name="emptyChat"
            >
                <div class="kgr-region">
                    <span class="kgr-region kgr-region--lightGray kgr-region--rounded kgr-centered kgr-centered--vertically kgr-centered--horizontally">{{
                        $t('Chat auswählen')
                    }}</span>
                </div>
            </slot>
        </div>
        <kgr-chat-dialog
            v-if="chatDialogEnabled"
            :show-dialog="showChatDialog"
            :institution-id="institutionId"
            :institution-key="institutionKey"
            :chat="editChat"
            @closeDialog="closeChatDialog"
            @newChat="setNewChat($event)"
            @updateName="updateChatName($event)"
        />
    </div>
</template>

<script>
import Badge from '../common/Badge.vue';
import Button from '../common/form/button/KgrButton.vue';
import ChatDialog from './ChatDialog.vue';
import ChatEntry from '../../js/dto/ChatEntry.js';
import ChatRepository from '../../js/repository/ChatRepository.js';
import ChatView from './ChatView.vue';
import ChatViewRepository from '../../js/repository/ChatViewRepository.js';
import DateLocale from '../../js/date/DateLocale.js';
import Error from '../../js/model/Error.js';
import GenericSelect from '../common/form/select/GenericSelect.vue';
import Icon from '../common/icon/Icon.vue';
import Loading from '../common/Loading.vue';
import {formatDistanceToNow} from '@legacy/js/date/date-fns.js'
import { getLanguageFromCookie } from '../../js/service/LanguageHelper.js';

const INSTITUTION_KINDERGARTEN = 'kindergarten';
const INSTITUTION_CLUB = 'club';

const ORDER_ALPHABETICALLY = 'alphabetically';
const ORDER_NEWEST = 'newest';
const FILTER_ALL_CHATS = 'all';
const FILTER_GROUP_CHATS = 'group';
const FILTER_SINGLE_CHATS = 'single';
const FILTER_TERMINATED_CHATS = 'terminated';

export default {
    components: {
        'kgr-badge': Badge,
        'kgr-button': Button,
        'kgr-chat-view': ChatView,
        'kgr-chat-dialog': ChatDialog,
        'kgr-generic-select': GenericSelect,
        'kgr-icon': Icon,
        'kgr-loading': Loading,
    },
    props: {
        initialChatId: {
            type: Number,
            required: false,
            default: null
        },
        institutionId: {
            type: [Number, String],
            required: true,
        },
        institutionKey: {
            type: String,
            required: true,
        },
        interval: {
            type: Number,
            default: 60,
        },
        author: {
            type: String,
            required: false,
            default: null
        },
        userId: {
            type: Number,
            required: false,
            default: null,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        }
    },
    data() {
        return {
            selectedChat: null,
            selectedChatId: null,
            editChat: null,
            chatList: [],
            unfilteredChatList: [],
            loading: true,
            chatListInterval: null,
            ignoreInitialChat: false,
            search: '',
            collator: null,
            showChatDialog: false,
            orderOptions: [
                {name: this.$t('Alphabetisch'), value: ORDER_ALPHABETICALLY},
                {name: this.$t('Neueste Nachrichten'), value: ORDER_NEWEST},
            ],
            selectedOrder: null,
            filterOptions: [
                {name: this.$t('Alle Chats'), value: FILTER_ALL_CHATS},
                {name: this.$t('Gruppenchats'), value: FILTER_GROUP_CHATS},
                {name: this.$t('Einzelchats'), value: FILTER_SINGLE_CHATS},
                {name: this.$t('Beendete Chats'), value: FILTER_TERMINATED_CHATS},
            ],
            selectedFilter: {name: this.$t('Alle Chats'), value: FILTER_ALL_CHATS},
        }
    },
    computed: {
        dateLocale() {
            return DateLocale.getLocale(this.translateTo);
        },
        chatDialogEnabled() {
            return this.isKindergartenChat && this.isCreatable;
        },
        tCreateChat() {
            return this.$t('Neuen Chat anlegen');
        },
        tSelectOrder() {
            return this.$t('Sortierung');
        },
        tSelectFilter() {
            return this.$t('Filter');
        },
        tEditChat() {
            return this.$t('Chat bearbeiten');
        },
        tTerminateChat() {
            return this.$t('Chat beenden');
        },
        tDeleteChat() {
            return this.$t('Chat löschen');
        },
        isKindergartenChat() {
            return this.institutionKey === INSTITUTION_KINDERGARTEN;
        },
    },
    mounted() {
        this.translateTo = getLanguageFromCookie();
        this.collator = new Intl.Collator(this.dateLocale?.code, {numeric: true, sensitivity: 'base'});
        this.selectedChatId = this.initialChatId;
        if (this.isKindergartenChat) {
            this.loadPreferencesFromLocalStorage();
        } else {
            this.selectedOrder = this.orderOptions[0];
        }
        let self = this;
        self.load();
        self.chatListInterval = setInterval(self.load, self.interval * 1000);
    },
    beforeMount() {
        clearTimeout(this.chatListInterval);
    },
    methods: {
        load() {
            ChatRepository.findAll(this.institutionKey, this.institutionId, this.search)
                .then(chats => {
                    this.chatList = this.unfilteredChatList = chats;
                    this.filterAndReorderChatList();
                    if (this.selectedChatId && !this.ignoreInitialChat) {
                        const initiallyOpenChat = this.chatList.find(chat => chat.id === this.selectedChatId);
                        if (initiallyOpenChat) {
                            this.openChat(initiallyOpenChat);
                        }
                    }
                    this.ignoreInitialChat = true;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                    if (this.chatListInterval) {
                        clearTimeout(this.chatListInterval);
                    }
                    this.chatListInterval = setTimeout(this.load, 30000);
                });
        },
        updateChatListByNewMessage() {
            this.selectedChat.lastMessageCreatedAt = new Date().getTime();
            this.reorderChatList();
        },
        orderChatListByLastMessageCreatedAt() {
            this.chatList = this.chatList.slice().sort((a, b) => {
                if (a.isNewSingleChat && a.createdAt) {
                    a.lastMessageCreatedAt = a.createdAt;
                }
                if (b.isNewSingleChat && b.createdAt) {
                    b.lastMessageCreatedAt = b.createdAt;
                }
                if (!a.lastMessageCreatedAt && !b.lastMessageCreatedAt) {
                    return 0;
                }
                if (!a.lastMessageCreatedAt) {
                    return 1;
                }
                if (!b.lastMessageCreatedAt) {
                    return -1;
                }
                return new Date(b.lastMessageCreatedAt) - new Date(a.lastMessageCreatedAt);
            });
        },
        orderChatListAlphabetically() {
            this.chatList.sort((l, r) => this.collator.compare(l.name, r.name));
        },
        reorderChatList() {
            if (this.selectedOrder.value === ORDER_ALPHABETICALLY) {
                this.orderChatListAlphabetically();
            } else if (this.selectedOrder.value === ORDER_NEWEST) {
                this.orderChatListByLastMessageCreatedAt();
            }
            this.savePreferencesToLocalStorage();
        },
        filterAndReorderChatList() {
            if (this.selectedFilter.value === FILTER_ALL_CHATS) {
                this.chatList = this.unfilteredChatList;
            } else if (this.selectedFilter.value === FILTER_GROUP_CHATS) {
                this.chatList = this.unfilteredChatList.filter(chat => chat.isGroupChat());
            } else if (this.selectedFilter.value === FILTER_SINGLE_CHATS) {
                this.chatList = this.unfilteredChatList.filter(chat => chat.isSingleChat());
            } else if (this.selectedFilter.value === FILTER_TERMINATED_CHATS) {
                this.chatList = this.unfilteredChatList.filter(chat => chat.isTerminated());
            }
            this.reorderChatList();
        },
        onSearch() {
            const strokesPerMinute = 200;

            if (this.chatListInterval) {
                clearTimeout(this.chatListInterval);
            }
            this.chatListInterval = setTimeout(() => this.load(), 1000 / (strokesPerMinute / 60));
        },
        getChatUrl(chatId) {
            switch (this.institutionKey) {
                case INSTITUTION_CLUB:
                    return `/backend/clubs/chat/${chatId}?id=${this.institutionId}`;
                case INSTITUTION_KINDERGARTEN:
                    return `/backend/chat/${chatId}/chat?id=${this.institutionId}`;
                default:
                    throw new Error(`Invalid institution key: ${this.institutionKey}`);
            }
        },
        onChatListEntryClick(e, chat) {
            // open in new tab
            if (!e.ctrlKey) {
                e.preventDefault();
                this.openChat(chat);
            }
        },
        openChat(chat) {
            this.selectedChat = chat;
            this.selectedChatId = chat.id;
            ChatViewRepository.update(this.institutionKey, chat.id)
                .then(() => {
                    chat.lastViewAt = new Date().getTime();
                    chat.count = 0;
                    this.$forceUpdate();
                });
        },
        chatListItemClasses(key) {
            let classes = ['kgr-verticalMenu__item'];
            if (this.selectedChat && this.selectedChat.id === key) {
                classes.push('is-active');
            }
            return classes.join(' ');
        },
        relativeTime(date) {
            return formatDistanceToNow(date, {locale: this.dateLocale, addSuffix: true});
        },
        openNewChatDialog() {
            this.editChat = null;
            this.showChatDialog = true;
        },
        openEditChatDialog(chat) {
            this.editChat = chat;
            this.showChatDialog = true;
        },
        closeChatDialog() {
            this.showChatDialog = false;
        },
        setNewChat(newChatId) {
            this.selectedChatId = Number(newChatId);
            this.ignoreInitialChat = false;
            if (this.selectedFilter.value === FILTER_GROUP_CHATS) {
                this.selectedFilter = {name: this.$t('Alle Chats'), value: FILTER_ALL_CHATS};
            }
            this.load();
        },
        terminateChat(chatToTerminate) {
            if (!this.isKindergartenChat) {
                return;
            }
            showConfirm(this.$t('Chat beenden'), this.$t('chat.terminate.confirm', {'name': chatToTerminate.name}), () => {
                ChatRepository.terminate(chatToTerminate.id)
                    .then(() => {
                        this.chatList.forEach(chat => {
                            if (chatToTerminate.id === chat.id) {
                                chat.terminatedAt = new Date().getTime();
                                this.$forceUpdate();
                            }
                        });
                        this.$notify.success('Chat wurde beendet');
                    })
                    .catch(() => this.$notify.error('Chat konnte nicht beendet werden'));
            }, 'Ok');
        },
        deleteChat(chatToDelete) {
            if (!this.isKindergartenChat) {
                return;
            }
            showConfirm(this.$t('Chat löschen'), this.$t('chat.delete.confirm', {'name': chatToDelete.name}), () => {
                ChatRepository.delete(chatToDelete.id)
                    .then(() => {
                        this.$notify.success('Der Chat wurde gelöscht');
                        if (this.selectedChatId === chatToDelete.id) {
                            this.resetSelectedChat();
                        }
                        this.chatList = this.chatList.filter(chat => chatToDelete.id !== chat.id);
                    })
                    .catch(() => this.$notify.error('Der Chat konnte nicht gelöscht werden'));
            }, 'Ok');
        },
        updateChatName(newName) {
            this.editChat.name = newName;
        },
        resetSelectedChat() {
            this.selectedChat = null;
            this.selectedChatId = null;
        },
        showEditIcon(chat) {
            return this.isCreatable && chat.isSingleChat() && chat.isEditable;
        },
        showTerminateIcon(chat) {
            return this.isDeletable && chat.isSingleChat() && chat.isEditable && !chat.isTerminated();
        },
        showDeleteIcon(chat) {
            return this.isDeletable && chat.isSingleChat() && chat.isDeletable;
        },
        concatUsers(chat) {
            if (chat.users && chat.users.length > 0) {
                return chat.users.join(', ');
            }
            return '';
        },
        concatAndShortenUsers(chat) {
            const users = this.concatUsers(chat);
            if (users.length <= ChatEntry.NAME_MAX_LENGTH) {
                return users;
            }
            return users.substring(0, ChatEntry.NAME_MAX_LENGTH - 4) + ' ...';
        },
        displayAllUsers(chat) {
            const users = this.concatUsers(chat);
            if (users.length >= ChatEntry.NAME_MAX_LENGTH) {
                return users;
            }
            return '';
        },
        getStorageKey() {
            return `chat.preferences.${this.institutionKey}.${this.userId}`;
        },
        savePreferencesToLocalStorage() {
            if (this.isKindergartenChat) {
                try {
                    const preferences = {
                        order: this.selectedOrder.value,
                    };
                    localStorage.setItem(this.getStorageKey(), JSON.stringify(preferences));
                } catch (error) {
                    console.error('Failed to save preferences:', error);
                }
            }
        },
        loadPreferencesFromLocalStorage() {
            try {
                const saved = localStorage.getItem(this.getStorageKey());
                if (saved) {
                    const preferences = JSON.parse(saved);
                    const savedOrder = this.orderOptions.find(o => o.value === preferences.order);
                    if (savedOrder) {
                        this.selectedOrder = savedOrder;
                        return;
                    }
                }
                this.selectedOrder = this.orderOptions[1];
            } catch (error) {
                console.error('Failed to load preferences:', error);
            }
        },
    }
}
</script>

<style scoped lang="scss">
.kgr-chat {
    height: 100vh;
    max-height: 80vh;
}
.chatlist {
    display: flex;
    flex-direction: column;
    .chatSearch {
        flex: 0 0 auto;
    }
    .chatSearchResults {
        flex: 1 1 100%;
    }
}
.addButton {
    margin-left: 10px;
}
.badgeColor {
    background-color: #ffb400;
}
</style>
